/**
 * Sponsored Voices list for a person — shown when Agents as Users is enabled.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mic2 } from 'lucide-react';
import { useAgentsAsUsersEnabled, VoiceBadge } from '../../../features/voices';
import { useVoicesStore } from '../../../features/voices/voicesStore';
import type { WorkspaceVoice } from '../../../features/voices/types';
import { getItemCountState } from '../../../components/collection/types';

export function SponsoredVoicesSection({
  workspaceId,
  userId,
}: {
  workspaceId: string | null;
  userId: string;
}) {
  const enabled = useAgentsAsUsersEnabled();
  const fetchSponsored = useVoicesStore((s) => s.fetchSponsored);
  const [voices, setVoices] = useState<WorkspaceVoice[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !workspaceId || !userId) return;
    setLoading(true);
    void fetchSponsored(workspaceId, userId).then((data) => {
      setVoices(data);
      setLoading(false);
    });
  }, [enabled, workspaceId, userId, fetchSponsored]);

  if (!enabled) return null;

  const countState = getItemCountState(voices.length);

  return (
    <div className="border border-charcoal-200 bg-[var(--bg-raised)] p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-charcoal-900">
          Sponsored Voices
          <VoiceBadge />
        </h4>
        <Link
          to="/prioritization/settings/voices"
          className="text-xs text-primary-600 no-underline hover:underline"
        >
          Manage
        </Link>
      </div>
      {loading && <p className="text-xs text-charcoal-500">Loading…</p>}
      {!loading && countState === 'zero' && (
        <p className="text-xs text-charcoal-500">
          This person does not sponsor any Voices yet.
        </p>
      )}
      {!loading && countState !== 'zero' && (
        <ul className="space-y-2">
          {voices.map((v) => (
            <li key={v.id}>
              <Link
                to={`/prioritization/settings/voices/${v.id}`}
                className="flex items-center gap-2 text-sm text-charcoal-800 no-underline hover:text-primary-700"
              >
                <Mic2 className="h-3.5 w-3.5 text-electric-mustard-600" />
                {v.displayName}
                <VoiceBadge label={v.badgeLabel || 'Voice'} />
                <span className="text-[11px] text-charcoal-400">{v.status}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
